import { useState, useCallback, useRef, useEffect } from 'react';
import { Attendee } from '@/types/attendee';
import { supabase } from '@/lib/supabase/client';
import { useToast } from '@/hooks/use-toast';

/**
 * Core hook for managing attendee data - provides basic CRUD operations
 * Enhanced with more reliable direct database operations and performance optimizations
 */
export const useAttendeeCore = () => {
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  
  const activeRequests = useRef<AbortController[]>([]);
  const attendeeCache = useRef<Record<string, Attendee>>({});
  const lastFullFetch = useRef<number>(0);
  const lastRequestTime = useRef<number>(0);
  const REQUEST_DEBOUNCE_MS = 300;
  const QUERY_TIMEOUT_MS = 10000;
  const CACHE_TTL_MS = 30000;

  const cleanupConnections = useCallback(() => {
    while (activeRequests.current.length > 3) {
      const oldController = activeRequests.current.shift();
      if (oldController) {
        try {
          oldController.abort();
        } catch (error) {
          // Ignore abort errors
        }
      }
    }
  }, []);

  const getAttendee = useCallback(async (code: string, bypassCache = false): Promise<Attendee | null> => {
    if (!bypassCache && attendeeCache.current[code]) {
      return attendeeCache.current[code];
    }
    
    try {
      cleanupConnections();
      
      const controller = new AbortController();
      activeRequests.current.push(controller);
      
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 5000);
      
      const { data, error } = await supabase
        .from('attendees')
        .select('code, name, email, phone, class, contribution, registered_at, attended, checked_in_at, paid, t_shirt_size')
        .eq('code', code)
        .single();
      
      clearTimeout(timeoutId);
      
      if (error) {
        throw error;
      }
      
      if (!data) {
        return null;
      }
      
      const sanitizedData: Attendee = {
        code: data.code,
        name: data.name,
        email: data.email,
        phone: data.phone,
        class: data.class,
        contribution: data.contribution,
        registeredAt: data.registered_at,
        attended: Boolean(data.attended),
        checkedInAt: data.checked_in_at || null,
        paid: Boolean(data.paid),
        t_shirt_size: data.t_shirt_size || null
      };
      
      attendeeCache.current[code] = sanitizedData;
      
      return sanitizedData;
    } catch (error) {
      console.error(`Error fetching attendee ${code}:`, error);
      return null;
    } finally {
      const controllerIndex = activeRequests.current.findIndex(
        controller => controller === activeRequests.current[activeRequests.current.length - 1]
      );
      
      if (controllerIndex !== -1) {
        activeRequests.current.splice(controllerIndex, 1);
      }
    }
  }, [cleanupConnections]);

  const loadAttendees = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && now - lastFullFetch.current < CACHE_TTL_MS) {
      return attendees;
    }
    
    if (now - lastRequestTime.current < REQUEST_DEBOUNCE_MS) {
      return attendees;
    }
    
    lastRequestTime.current = now;
    lastFullFetch.current = now;
    
    try {
      setIsLoading(true);
      cleanupConnections();
      
      const controller = new AbortController();
      activeRequests.current.push(controller);
      
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, QUERY_TIMEOUT_MS);
      
      const { data, error } = await supabase
        .from('attendees')
        .select('code, name, email, phone, class, contribution, registered_at, attended, checked_in_at, paid, t_shirt_size')
        .order('registered_at', { ascending: false });
      
      clearTimeout(timeoutId);
      
      if (error) {
        throw error;
      }
      
      const sanitizedData = (data || []).map(item => ({
        code: item.code,
        name: item.name,
        email: item.email,
        phone: item.phone,
        class: item.class,
        contribution: item.contribution,
        registeredAt: item.registered_at,
        attended: Boolean(item.attended),
        checkedInAt: item.checked_in_at || null,
        paid: Boolean(item.paid),
        t_shirt_size: item.t_shirt_size || null
      }));
      
      sanitizedData.forEach(item => {
        attendeeCache.current[item.code] = item;
      });
      
      setAttendees(sanitizedData);
      return sanitizedData;
    } catch (error) {
      if (error.message !== 'AbortError' && error.message !== 'The operation was aborted') {
        toast({
          title: "Lỗi tải dữ liệu",
          description: "Không thể tải danh sách người tham dự. Vui lòng thử lại sau.",
          variant: "destructive"
        });
      }
      
      return attendees;
    } finally {
      setIsLoading(false);
      
      const controllerIndex = activeRequests.current.findIndex(
        controller => controller === activeRequests.current[activeRequests.current.length - 1]
      );
      
      if (controllerIndex !== -1) {
        activeRequests.current.splice(controllerIndex, 1);
      }
    }
  }, [attendees, cleanupConnections, toast]);

  const updateAttendee = useCallback(async (code: string, updates: Partial<Attendee>) => {
    if (!code || Object.keys(updates).length === 0) {
      return null;
    }
    
    try {
      cleanupConnections();
      
      const controller = new AbortController();
      activeRequests.current.push(controller);
      
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, QUERY_TIMEOUT_MS);
      
      const existingAttendee = attendeeCache.current[code];
      
      const formattedUpdates: Record<string, any> = {};
      
      if (updates.attended !== undefined) formattedUpdates.attended = updates.attended;
      if (updates.checkedInAt !== undefined) formattedUpdates.checked_in_at = updates.checkedInAt;
      if (updates.paid !== undefined) formattedUpdates.paid = updates.paid;
      
      let result = null;
      
      if (updates.attended === true) {
        const timestamp = updates.checkedInAt || new Date().toISOString();
        const { data: rpcData, error: rpcError } = await supabase
          .rpc('confirm_attendance', {
            attendee_code: code,
            check_in_time: timestamp
          });
          
        clearTimeout(timeoutId);
        
        if (!rpcError && rpcData) {
          const updatedAttendee: Attendee = {
            ...(existingAttendee || {
              code,
              name: '',
              email: '',
              phone: '',
              class: '',
              contribution: 0,
              registeredAt: '',
              paid: false
            }),
            attended: true,
            checkedInAt: timestamp
          };
          
          attendeeCache.current[code] = updatedAttendee;
          
          return updatedAttendee;
        }
      }
      
      const { data, error } = await supabase
        .from('attendees')
        .update(formattedUpdates)
        .eq('code', code)
        .select('code, name, email, phone, class, contribution, registered_at, attended, checked_in_at, paid')
        .single();
      
      clearTimeout(timeoutId);
      
      if (error) {
        throw error;
      }
      
      if (!data) {
        throw new Error('No data returned from update operation');
      }
      
      const updatedAttendee: Attendee = {
        code: data.code,
        name: data.name,
        email: data.email,
        phone: data.phone,
        class: data.class,
        contribution: data.contribution,
        registeredAt: data.registered_at,
        attended: Boolean(data.attended),
        checkedInAt: data.checked_in_at,
        paid: Boolean(data.paid)
      };
      
      attendeeCache.current[code] = updatedAttendee;
      
      return updatedAttendee;
    } catch (error) {
      if (error.message !== 'AbortError' && error.message !== 'The operation was aborted') {
        toast({
          title: "Lỗi cập nhật",
          description: "Không thể cập nhật thông tin người tham dự. Vui lòng thử lại sau.",
          variant: "destructive"
        });
      }
      
      return null;
    } finally {
      const controllerIndex = activeRequests.current.findIndex(
        controller => controller === activeRequests.current[activeRequests.current.length - 1]
      );
      
      if (controllerIndex !== -1) {
        activeRequests.current.splice(controllerIndex, 1);
      }
    }
  }, [cleanupConnections, toast]);

  useEffect(() => {
    return () => {
      activeRequests.current.forEach(controller => {
        try {
          controller.abort();
        } catch (error) {
          // Ignore abort errors
        }
      });
      activeRequests.current = [];
    };
  }, []);

  return {
    attendees,
    isLoading,
    usingSupabase: true,
    loadAttendees,
    updateAttendee,
    getAttendee,
    setAttendees
  };
};
